import { convertLegacyAppRoot } from '@backstage/core-compat-api';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { createApp } from '@backstage/frontend-defaults';
import {
  AppRootElementBlueprint,
  createFrontendModule,
} from '@backstage/frontend-plugin-api';
import {
  Avatar,
  Content,
  Header,
  OverflowTooltip,
  SignInPage,
} from '@backstage/core-components';
import { ApiExplorerPage, apiDocsPlugin } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  CatalogTable,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { orgPlugin } from '@backstage/plugin-org';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { SearchPage } from '@backstage/plugin-search';
import {
  TechDocsIndexPage,
  TechDocsReaderPage,
  techdocsPlugin,
} from '@backstage/plugin-techdocs';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { UserSettingsPage } from '@backstage/plugin-user-settings';
import { defaultTypography, UnifiedThemeProvider } from '@backstage/theme';
import { Chip, Grid } from '@material-ui/core';
import { TvipPage } from '@traton/backstage-plugin-tvip';
import {
  DeveloperportalsupportagentPage,
  PortalAssistantIndexStatusPage,
} from '@internal/backstage-plugin-developer-portal-support-agent';
import { PortalgroupsPage } from '@internal/backstage-plugin-portalgroups';
import { backstageThemeDark, backstageThemeLight } from '@traton/common-ui';
import { VtabPage } from '@traton/vtab-plugin';
import { Mermaid } from 'backstage-plugin-techdocs-addon-mermaid';
import { icons as logos } from '@iconify-json/logos';
import { XkcdComicCard } from 'backstage-plugin-xkcd';
import { Navigate, Route } from 'react-router-dom';
import { apis, providersApiRefs } from './apis';
import { CustomCatalogImportPage } from './components/CatalogImport';
import { entityPage } from './components/catalog/EntityPage';
import { GoldenPathSubHeader } from './components/golden-paths';
import { Root } from './components/Root';
import { SanitizedTextFieldExtension } from './components/scaffolder';
import { searchPage } from './components/search/SearchPage';
import { TechDocsSubHeader } from './components/techdocs/TechDocsSubHeader';
import { TechDocsEditButton } from './components/techdocs/TechDocsEditButton';
import { TechDocsReaderLayoutWithFeedback } from './components/techdocs/TechDocsFeedbackAddon';
import { TechDocsFeedbackPage } from '@internal/plugin-techdocs-feedback';
import { TechDocsImageZoom } from './components/techdocs/TechDocsImageZoom';
import { ToolingPage } from './components/tooling/ToolingPage';
import { DeveloperPlatformPage } from './components/tooling/DeveloperPlatformPage';
import { plugins } from './plugins';
import { apiDocsTranslationsOverrideExtension } from './translations/api-docs';
import { coreComponentsTranslationsOverrideExtension } from './translations/core-components';
import { AgenticMarketplacePage } from '@internal/backstage-plugin-agentic-marketplace';
import { TechdocplaygroundPage } from '@internal/backstage-plugin-techdoc-playground';
import {
  catalogApiRef,
  EntityKindPicker,
  EntityLifecyclePicker,
  EntityNamespacePicker,
  EntityOwnerPicker,
  EntityProcessingStatusPicker,
  EntityTagPicker,
  EntityTypePicker,
  UserListPicker,
} from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import { useEffect, useState } from 'react';
import { OaslaunchpadPage } from '@traton/backstage-plugin-oas-launchpad';
import { DevToolsPage } from '@backstage/plugin-devtools';
import { customDevToolsPage } from './components/devtools/CustomDevToolsPage';
import {
  SignInPageBlueprint,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import {
  AnnouncementPage,
  AnnouncementBanner,
} from '@internal/backstage-plugin-announcement';

import appPlugin from '@backstage/plugin-app';
import authPlugin from '@backstage/plugin-auth';

const CustomEntityLifecyclePicker = ({
  excludedLifecycles,
}: {
  excludedLifecycles?: string[];
}) => {
  const catalogApi = useApi(catalogApiRef);
  const [lifecycles, setLifecycles] = useState<string[]>([]);

  useEffect(() => {
    let mounted = true;
    catalogApi
      .getEntityFacets({ facets: ['spec.lifecycle'] })
      .then(response => {
        if (!mounted) return;
        const distinctLifecycles = response.facets['spec.lifecycle']
          .map(lifecycleFacet => lifecycleFacet.value)
          .reduce<string[]>((list, lc) => {
            if (!list.some(item => item.toLowerCase() === lc.toLowerCase())) {
              list.push(lc);
            }
            return list;
          }, [])
          .filter(lifecycle => !excludedLifecycles?.includes(lifecycle));

        setLifecycles(distinctLifecycles);
      });
    return () => {
      mounted = false;
    };
  }, [catalogApi, excludedLifecycles]);

  return (
    <EntityLifecyclePicker
      key={lifecycles.join(',')}
      initialFilter={lifecycles}
    />
  );
};

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <DeveloperportalsupportagentPage />
    </Route>
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/settings" element={<UserSettingsPage />} />
    <Route path="/agentic-marketplace/*" element={<AgenticMarketplacePage />} />
    <Route path="/tvip" element={<TvipPage />} />
    <Route path="/vtab" element={<VtabPage />} />
    <Route path="/techdoc-editor" element={<TechdocplaygroundPage />} />
    <Route
      path="/portal-assistant"
      element={<Navigate replace to="/developer-assistant" />}
    />
    <Route
      path="/developer-assistant/*"
      element={<DeveloperportalsupportagentPage />}
    />
    <Route
      path="/developer-assistant/status"
      element={<PortalAssistantIndexStatusPage />}
    />
    <Route path="/groups" element={<PortalgroupsPage />} />
    <Route
      path="/xkcd"
      element={
        <>
          <Header title="Comics" />
          <Content>
            <Grid container justifyContent="flex-start" spacing={6}>
              <Grid item xs={12} md={8}>
                <XkcdComicCard />
              </Grid>
            </Grid>
          </Content>
        </>
      }
    />
    <Route
      path="/docs"
      element={
        <TechDocsIndexPage key="docs" CustomHeader={TechDocsSubHeader} />
      }
    />
    <Route
      path="/golden-paths"
      element={
        <TechDocsIndexPage
          CustomHeader={GoldenPathSubHeader}
          key="golden-paths"
        />
      }
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={
        <TechDocsReaderPage
          overrideThemeOptions={{
            typography: defaultTypography,
          }}
        />
      }
    >
      <TechDocsReaderLayoutWithFeedback />
      <TechDocsAddons>
        <TechDocsEditButton />
        <ReportIssue />
        <TechDocsImageZoom />
        <Mermaid
          iconLoaders={[
            {
              name: logos.prefix,
              icons: logos,
            },
          ]}
        />
      </TechDocsAddons>
    </Route>
    <Route path="/techdocs-feedback" element={<TechDocsFeedbackPage />} />
    <Route path="/create" element={<ScaffolderPage />}>
      <ScaffolderFieldExtensions>
        <SanitizedTextFieldExtension />
      </ScaffolderFieldExtensions>
    </Route>
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route path="/catalog-import" element={<CatalogImportPage />}>
      <RequirePermission permission={catalogEntityCreatePermission}>
        <CustomCatalogImportPage />
      </RequirePermission>
    </Route>
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/tooling" element={<ToolingPage />} />
    <Route
      path="/tooling/developer-platform"
      element={<DeveloperPlatformPage />}
    />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route
      path="/catalog"
      element={
        <CatalogIndexPage
          pagination={{ mode: 'offset', limit: 20 }}
          columns={entityListContext => [
            {
              sorting: false,
              width: '50px',
              hidden: entityListContext.filters.kind?.value !== 'user',
              render: ({ entity }) => (
                <Avatar
                  picture={
                    (entity.spec?.profile as { picture?: string })?.picture
                  }
                  displayName={
                    (entity.spec?.profile as { displayName?: string })
                      ?.displayName
                  }
                />
              ),
            },
            ...CatalogTable.defaultColumnsFunc(entityListContext)
              .filter(p => p.field !== 'entity.spec.lifecycle')
              .filter(p => p.field !== 'entity.metadata.description')
              .filter(p => p.field !== 'entity.metadata.tags'),
            {
              title: 'User Email',
              field: 'entity.spec.profile.email',
              hidden: entityListContext.filters.kind?.value !== 'user',
              render: ({ entity }) => (
                <OverflowTooltip
                  text={
                    (entity.spec?.profile as { email?: string })?.email || 'N/A'
                  }
                  placement="bottom-start"
                />
              ),
            },
            {
              ...CatalogTable.defaultColumnsFunc(entityListContext).find(
                p => p.field === 'entity.metadata.tags',
              ),
              render: ({ entity }) => (
                <Content noPadding className="catalog-tags-container">
                  {entity.metadata.tags?.map(tag => (
                    <Chip size="small" className="catalog-tags" label={tag} />
                  ))}
                </Content>
              ),
            },
          ]}
          filters={
            <>
              <EntityKindPicker />
              <EntityTypePicker />
              <UserListPicker />
              <EntityOwnerPicker />
              <CustomEntityLifecyclePicker
                excludedLifecycles={['experimental']}
              />
              <EntityTagPicker />
              <EntityProcessingStatusPicker />
              <EntityNamespacePicker />
            </>
          }
        />
      }
    />
    <Route path="/oas-launchpad" element={<OaslaunchpadPage />} />
    <Route path="/devtools" element={<DevToolsPage />}>
      {customDevToolsPage}
    </Route>
    <Route path="/announcement" element={<AnnouncementPage />} />
  </FlatRoutes>
);

// TODO: When convertLegacyAppRoot is removed, the BUIProvider should be moved wrap the <Root> component
const convertedRootFeatures = convertLegacyAppRoot(
  <AppRouter>
    <Root>{routes}</Root>
  </AppRouter>,
);

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        <SignInPage
          {...props}
          auto
          provider={{
            id: providersApiRefs.microsoftProviderAuthApiRef.id,
            title: 'Microsoft',
            message: 'Sign in using Entra ID',
            apiRef: providersApiRefs.microsoftProviderAuthApiRef,
          }}
        />
      ),
  },
});

const tratonLightTheme = ThemeBlueprint.make({
  name: 'tratonThemeLight',
  params: {
    theme: {
      id: 'tratonThemeLight',
      title: 'Light Mode',
      variant: 'light',
      Provider: ({ children }) => (
        <div className="tl-mode-light" data-theme-mode="light">
          <UnifiedThemeProvider
            theme={backstageThemeLight}
            children={children}
          />
        </div>
      ),
    },
  },
});

const tratonDarkTheme = ThemeBlueprint.make({
  name: 'tratonThemeDark',
  params: {
    theme: {
      id: 'tratonThemeDark',
      title: 'Dark Mode',
      variant: 'dark',
      Provider: ({ children }) => (
        <div className="tl-mode-dark" data-theme-mode="dark">
          <UnifiedThemeProvider
            theme={backstageThemeDark}
            children={children}
          />
        </div>
      ),
    },
  },
});

const appDefaultThemesDisableOverrides = appPlugin.withOverrides({
  extensions: [
    appPlugin.getExtension('theme:app/light').override({
      disabled: true,
    }),
    appPlugin.getExtension('theme:app/dark').override({
      disabled: true,
    }),
  ],
});

// Renders public announcements to unauthenticated users (sign-in page).
// hideWhenAuthenticated causes it to unmount once sign-in completes so that
// the in-app banner inside Root.tsx takes over without showing a duplicate.
const announcementBannerElement = AppRootElementBlueprint.make({
  name: 'announcement-banner',
  params: {
    element: <AnnouncementBanner hideWhenAuthenticated />,
  },
});

const customModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    coreComponentsTranslationsOverrideExtension,
    apiDocsTranslationsOverrideExtension,
    ...apis,
    signInPage,
    tratonLightTheme,
    tratonDarkTheme,
    announcementBannerElement,
  ],
});

const app = createApp({
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  features: [
    appDefaultThemesDisableOverrides,
    customModule,
    ...convertedRootFeatures,
    ...plugins,
    authPlugin,
  ],
});

export default app.createRoot();
